/**
 * PlanGeoOverlay — plano georreferenciado sobre MapLibre.
 *
 * Componente unificado del sprint planograma (2026-06-12). Se usa en
 * dos contextos con la misma implementación pero modos distintos:
 *
 *   mode="edit"      — SiteConfigView, flujo de georreferenciar el plano.
 *                      4 handles draggables en las esquinas. El operador
 *                      arrastra cada esquina del plano hasta calzarlo
 *                      sobre el footprint real del edificio en el mapa OSM.
 *                      Cada cambio de handles dispara onGeoreferenceChange
 *                      con las 4 esquinas {nw, ne, sw, se} en lat/lon.
 *
 *   mode="navigate"  — MapView Q4, operación. Pan/zoom natural del mapa.
 *                      Las cámaras se renderizan como markers con cono de
 *                      visión (rotado por floor_plan_rotation_deg, apertura
 *                      por fov_horizontal_deg, alcance por effective_range_m
 *                      convertido a metros geo). Click en cámara dispara
 *                      onCameraSelect. Click en plano vacío dispara
 *                      onMapClick.
 *
 * El plano se renderiza como image source de MapLibre con 4 coords geo
 * (top-left, top-right, bottom-right, bottom-left). Eso lo hace nativo
 * sin geometría custom — MapLibre se encarga del re-render al pan/zoom.
 *
 * NO maneja selector de pisos — eso vive en el componente padre. Acá
 * solo se ve UN plano (un piso) por vez.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'


// ─── Style OSM básico ────────────────────────────────────────────────────
// Sin token, sin API key, sin licencia. tiles.openstreetmap.org es gratis
// para uso moderado. Si crece el volumen, switch a un tile server propio.

const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap',
      maxzoom: 19,
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
}


// ─── Helpers de coord ────────────────────────────────────────────────────

/**
 * Convierte el objeto georeference del backend al array de 4 coords que
 * MapLibre espera para image source: [TL, TR, BR, BL] cada uno [lng, lat].
 * (Convención MapLibre, no lat/lng como otras libs.)
 */
function cornersToImageCoords({ nw, ne, sw, se }) {
  return [
    [nw[1], nw[0]],  // top-left = NW
    [ne[1], ne[0]],  // top-right = NE
    [se[1], se[0]],  // bottom-right = SE
    [sw[1], sw[0]],  // bottom-left = SW
  ]
}


// ─── DOM para handles y markers ──────────────────────────────────────────

function createHandleEl(label) {
  const el = document.createElement('div')
  el.style.cssText = `
    width: 28px; height: 28px; border-radius: 50%;
    background: #0091ff; border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    cursor: move; display: flex; align-items: center;
    justify-content: center; color: white; font-size: 10px;
    font-weight: 700; user-select: none;
  `
  el.textContent = label
  return el
}

function createCameraEl({ channel, isSelected, hasIncident }) {
  const el = document.createElement('div')
  el.style.cssText = `
    width: ${isSelected ? 32 : 26}px;
    height: ${isSelected ? 32 : 26}px;
    border-radius: 50%;
    background: ${hasIncident ? '#d93025' : '#0091ff'};
    border: ${isSelected ? '3px' : '2px'} solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.5);
    cursor: pointer; display: flex; align-items: center;
    justify-content: center; color: white;
    font-size: ${isSelected ? 13 : 11}px;
    font-weight: 700; user-select: none;
    transition: all 0.15s ease;
  `
  el.textContent = String(channel)
  if (hasIncident) {
    el.style.animation = 'pulse-ring 1.5s ease-out infinite'
  }
  return el
}

/**
 * Crea el elemento SVG del cono de visión de la cámara, dibujado sobre
 * el mapa como un overlay relativo al marker. El cono se rota según
 * floor_plan_rotation_deg y se abre según fov_horizontal_deg.
 *
 * NOTA: alcance visual está en píxeles del mapa, no en metros reales.
 * Para alcance real necesitaríamos calcular el zoom actual + m/px del
 * mapa. Por ahora aproximamos para que el cono se vea proporcional.
 */
function createConeEl({ rotation_deg, fov_deg, range_px }) {
  const wrap = document.createElement('div')
  wrap.style.cssText = `
    position: absolute; pointer-events: none;
    width: ${range_px * 2}px; height: ${range_px * 2}px;
    transform: translate(-50%, -50%) rotate(${rotation_deg}deg);
    transform-origin: center;
    z-index: -1;
  `
  const svg = `
    <svg width="${range_px * 2}" height="${range_px * 2}"
         viewBox="0 0 ${range_px * 2} ${range_px * 2}"
         xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="cone-grad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="rgba(0,145,255,0.45)"/>
          <stop offset="100%" stop-color="rgba(0,145,255,0)"/>
        </radialGradient>
      </defs>
      <path
        d="M ${range_px} ${range_px}
           L ${range_px - range_px * Math.sin(fov_deg * Math.PI / 360)}
             ${range_px - range_px * Math.cos(fov_deg * Math.PI / 360)}
           A ${range_px} ${range_px} 0 0 1
             ${range_px + range_px * Math.sin(fov_deg * Math.PI / 360)}
             ${range_px - range_px * Math.cos(fov_deg * Math.PI / 360)}
           Z"
        fill="url(#cone-grad)"
        stroke="rgba(0,145,255,0.6)"
        stroke-width="1"
      />
    </svg>
  `
  wrap.innerHTML = svg
  return wrap
}


// ─── Inversa BILINEAL exacta ─────────────────────────────────────────────
//
// El backend persiste lat/lon usando proyección bilineal con las 4 esquinas:
//   top    = lerp(NW, NE, x)
//   bottom = lerp(SW, SE, x)
//   point  = lerp(top, bottom, y)
//
// Cuando el operador clickea sobre el plano queremos resolver el problema
// inverso: dado (lat, lon), encontrar (x_pct, y_pct). Eso no tiene forma
// cerrada simple porque el sistema es no-lineal en (x, y). Resolvemos con
// Newton-Raphson partiendo de (0.5, 0.5), 15 iteraciones bastan para
// converger a precisión de subpixel en planos de hasta 200m.
//
// Antes usábamos una inversa AFFINE (3 puntos: NW, NE, SW) que asume el
// plano como paralelogramo. Para planos trapezoidales o ligeramente
// rotados, esa aproximación da drift visible — la cámara queda fuera del
// plano renderizado aunque el backend almacena coords coherentes.
//
// Retorna null si el determinante del jacobiano se hace singular (planos
// degenerados) o si después de 15 iteraciones no converge.
function _invertBilinearLatLon(corners, targetLat, targetLon) {
  const c = corners
  // Forward bilineal: dado (x, y) → (lat, lon)
  const fwd = (x, y) => {
    const topLat = c.nw[0] + x * (c.ne[0] - c.nw[0])
    const topLon = c.nw[1] + x * (c.ne[1] - c.nw[1])
    const botLat = c.sw[0] + x * (c.se[0] - c.sw[0])
    const botLon = c.sw[1] + x * (c.se[1] - c.sw[1])
    return [
      topLat + y * (botLat - topLat),
      topLon + y * (botLon - topLon),
    ]
  }
  let x = 0.5, y = 0.5
  for (let i = 0; i < 15; i++) {
    const [lat, lon] = fwd(x, y)
    const eLat = lat - targetLat
    const eLon = lon - targetLon
    if (Math.abs(eLat) < 1e-9 && Math.abs(eLon) < 1e-9) break
    // Jacobiano analítico
    const dLatDx = (1 - y) * (c.ne[0] - c.nw[0]) + y * (c.se[0] - c.sw[0])
    const dLatDy = (1 - x) * (c.sw[0] - c.nw[0]) + x * (c.se[0] - c.ne[0])
    const dLonDx = (1 - y) * (c.ne[1] - c.nw[1]) + y * (c.se[1] - c.sw[1])
    const dLonDy = (1 - x) * (c.sw[1] - c.nw[1]) + x * (c.se[1] - c.ne[1])
    const det = dLatDx * dLonDy - dLatDy * dLonDx
    if (Math.abs(det) < 1e-20) return null
    // Newton step: nueva (x, y) = (x, y) - J⁻¹ * error
    const dx = (eLat * dLonDy - eLon * dLatDy) / det
    const dy = (eLon * dLatDx - eLat * dLonDx) / det
    x -= dx
    y -= dy
  }
  return { xPct: x, yPct: y }
}


// ─── Componente principal ────────────────────────────────────────────────

export default function PlanGeoOverlay({
  mode = 'navigate',                 // 'edit' | 'navigate'
  floorPlan,                          // { georeference: { nw, ne, sw, se, meters_per_pixel } }
  imageUrl,                           // URL del PNG del plano
  cameras = [],                       // [{ id, channel, lat, lon, floor_plan_rotation_deg, fov_horizontal_deg, effective_range_m }]
  selectedCameraId = null,
  incidentCameraIds = [],             // ids de cámaras con incidente activo (pulsantes)
  initialCenter,                      // { lat, lon } opcional
  initialZoom = 18,
  height = '100%',
  // Callbacks
  onGeoreferenceChange,               // edit: (newCorners) => void
  onCameraMove,                       // edit: (cameraId, x_pct, y_pct) => void
  onCameraDrop,                       // edit: (cameraId, x_pct, y_pct) — drop desde sidebar
  onCameraSelect,                     // navigate: (camera) => void
  onMapClick,                         // navigate: () => void
  // Click-to-place: cuando hay una cámara "armada" para colocar, el siguiente
  // click sobre el mapa la posiciona ahí y dispara onCameraPlaced. Si null,
  // los clicks van por el flujo normal.
  placingCameraId,
  onCameraPlaced,                     // (cameraId, x_pct, y_pct) => void
}) {
  const containerRef = useRef(null)
  const mapRef       = useRef(null)
  const handleMarkersRef = useRef({})      // { nw, ne, sw, se } → maplibregl.Marker
  const cameraMarkersRef = useRef({})       // { camId } → maplibregl.Marker
  const [mapReady, setMapReady] = useState(false)
  // Tracker: ya hicimos fitBounds inicial al primer image source? Si sí,
  // nunca más reposicionar el viewport (sino el mapa "salta" cada vez
  // que el operador arrastra un handle o re-seedeamos esquinas).
  const didInitialFitRef = useRef(false)

  // Working copy de las esquinas — se actualiza con drag y al final
  // se notifica al padre via onGeoreferenceChange.
  const [workingCorners, setWorkingCorners] = useState(
    floorPlan?.georeference || null,
  )
  // Ref para evitar closure stale en los handlers de drag/dragend. Los
  // markers MapLibre capturan el closure de cuando se crearon y usan una
  // versión vieja de workingCorners en dragend. Eso causaba que solo la
  // esquina actualmente arrastrada cambiara y las otras volvieran a
  // values stale. Mantenemos un ref siempre sincronizado.
  const workingCornersRef = useRef(workingCorners)
  useEffect(() => { workingCornersRef.current = workingCorners }, [workingCorners])
  // Refs para TODO lo que el click handler lee — el handler se registra
  // una vez en init y la closure original se queda con valores stale
  // si usamos las props directo.
  const placingCameraIdRef = useRef(placingCameraId)
  useEffect(() => {
    placingCameraIdRef.current = placingCameraId
    if (mapRef.current) {
      const canvas = mapRef.current.getCanvas()
      if (canvas) canvas.style.cursor = placingCameraId ? 'crosshair' : ''
    }
  }, [placingCameraId])
  const modeRef = useRef(mode)
  useEffect(() => { modeRef.current = mode }, [mode])
  const onCameraPlacedRef = useRef(onCameraPlaced)
  useEffect(() => { onCameraPlacedRef.current = onCameraPlaced }, [onCameraPlaced])
  const onMapClickRef = useRef(onMapClick)
  useEffect(() => { onMapClickRef.current = onMapClick }, [onMapClick])

  // Sincronizar con floorPlan externo cuando cambia
  useEffect(() => {
    if (floorPlan?.georeference) {
      setWorkingCorners(floorPlan.georeference)
    } else {
      setWorkingCorners(null)
    }
  }, [floorPlan?.id, floorPlan?.georeference])

  // ── Init MapLibre ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    // Centro inicial: prioridad center prop → centro del plano (si geo) →
    // centro de Chile
    const center = initialCenter
      ? [initialCenter.lon, initialCenter.lat]
      : workingCorners
        ? [
            (workingCorners.nw[1] + workingCorners.se[1]) / 2,
            (workingCorners.nw[0] + workingCorners.se[0]) / 2,
          ]
        : [-70.6483, -33.4569]   // Santiago default

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OSM_STYLE,
      center,
      zoom: initialZoom,
      maxZoom: 21,
      attributionControl: false,
    })
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')

    map.on('load', () => {
      mapRef.current = map
      setMapReady(true)
      // Debug: expone la instancia para inspección desde DevTools / Chrome MCP
      if (typeof window !== 'undefined') {
        window.__planMap = map
      }
      // Fix mapa oscuro al primer load: el SW workbox reinstala mientras
      // MapLibre inicializa el contexto WebGL, y los tiles no se piden
      // hasta un re-render externo. Forzamos resize + repaint en setTimeout
      // para que MapLibre re-mida el container y cargue los tiles visibles.
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize()
          mapRef.current.triggerRepaint()
        }
      }, 300)
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize()
          mapRef.current.triggerRepaint()
        }
      }, 1000)
    })

    // Click en mapa — handler registrado UNA VEZ. Lee todo via refs para
    // no quedarse con closures stale.
    map.on('click', (e) => {
      const m = modeRef.current
      const placingId = placingCameraIdRef.current
      const corners = workingCornersRef.current
      console.log('[PlanGeoOverlay] map click', {
        mode: m, placingId, hasCorners: !!corners, lngLat: e.lngLat,
      })
      if (m === 'navigate' && onMapClickRef.current) {
        onMapClickRef.current(e)
        return
      }
      if (m === 'edit' && placingId && onCameraPlacedRef.current && corners) {
        const c = corners
        // Inversa BILINEAL exacta (Newton-Raphson). El backend hace forward
        // bilineal con 4 esquinas; usar la inversa affine (3 esquinas) acá
        // causaba drift entre lo que cliqueás y donde renderiza la cámara.
        const pct = _invertBilinearLatLon(c, e.lngLat.lat, e.lngLat.lng)
        if (!pct) return
        const { xPct, yPct } = pct
        console.log('[PlanGeoOverlay] placing camera', placingId, 'at', {
          xPct: xPct.toFixed(4), yPct: yPct.toFixed(4),
          clickLat: e.lngLat.lat.toFixed(7), clickLon: e.lngLat.lng.toFixed(7),
        })
        // Rechazar clicks claramente fuera del plano (con margen 5% tolerancia).
        // Antes se hacía clamp a [0,1] y las cámaras quedaban pegadas a bordes,
        // lo que confundía al operador (cámaras invisibles fuera del viewport).
        if (xPct < -0.05 || xPct > 1.05 || yPct < -0.05 || yPct > 1.05) {
          console.warn('[PlanGeoOverlay] click fuera del plano — ignorado', { xPct, yPct })
          // Flash visual: cursor wiggle para que el operador note el rechazo.
          const canvas = mapRef.current?.getCanvas()
          if (canvas) {
            canvas.style.cursor = 'not-allowed'
            setTimeout(() => { if (canvas) canvas.style.cursor = placingCameraIdRef.current ? 'crosshair' : '' }, 600)
          }
          return
        }
        onCameraPlacedRef.current(
          placingId,
          Math.max(0, Math.min(1, xPct)),
          Math.max(0, Math.min(1, yPct)),
        )
      }
    })

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])  // init solo una vez — el resto se maneja con effects más abajo

  // ── flyTo cuando cambia initialCenter ──────────────────────────────────
  // El padre actualiza initialCenter cuando el operador elige una dirección
  // del buscador Nominatim. flyTo anima el viewport hacia esa coord.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !initialCenter) return
    mapRef.current.flyTo({
      center: [initialCenter.lon, initialCenter.lat],
      zoom: Math.max(mapRef.current.getZoom(), 17),
      duration: 1200,
    })
  }, [mapReady, initialCenter?.lat, initialCenter?.lon])

  // ── Image source del plano ─────────────────────────────────────────────
  // Cuando cambia workingCorners o imageUrl, agregamos/actualizamos el
  // image source del plano sobre el mapa.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !imageUrl || !workingCorners) return
    const map = mapRef.current
    const coords = cornersToImageCoords(workingCorners)

    const SRC_ID = 'floor-plan-img'
    const LAYER_ID = 'floor-plan-layer'

    if (map.getSource(SRC_ID)) {
      // Update existing
      map.getSource(SRC_ID).setCoordinates(coords)
      // setUrl si cambió la imagen
      // MapLibre no expone setUrl directo, hay que remover+agregar
    } else {
      map.addSource(SRC_ID, {
        type: 'image',
        url: imageUrl,
        coordinates: coords,
      })
      map.addLayer({
        id: LAYER_ID,
        type: 'raster',
        source: SRC_ID,
        paint: {
          // Opacidad baja en edit (0.4) para ver el mapa OSM debajo y
          // calzar bien las esquinas. Alta en navigate (0.85) para
          // privilegiar la lectura del plano.
          'raster-opacity': mode === 'edit' ? 0.4 : 0.85,
          'raster-fade-duration': 0,
        },
      })
      // Centrar la vista en el plano SOLO la primera vez que se agrega.
      // Despuéa de eso, NUNCA reposicionar — el operador necesita el mapa
      // quieto mientras arrastra esquinas y posiciona cámaras.
      if (!didInitialFitRef.current) {
        try {
          const lons = coords.map(c => c[0])
          const lats = coords.map(c => c[1])
          const bounds = [
            [Math.min(...lons), Math.min(...lats)],
            [Math.max(...lons), Math.max(...lats)],
          ]
          map.fitBounds(bounds, { padding: 60, duration: 800, maxZoom: 19 })
          didInitialFitRef.current = true
        } catch (e) { /* ignore */ }
      }
    }

    return () => {
      // No cleanup en cada render — solo al unmount el container
    }
  }, [mapReady, imageUrl, workingCorners, mode])

  // ── Handles draggables (modo edit) ─────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !mapRef.current) return
    const map = mapRef.current

    // Remover handles existentes
    Object.values(handleMarkersRef.current).forEach(m => m.remove())
    handleMarkersRef.current = {}

    if (mode !== 'edit' || !workingCorners) return

    // Crear handles para las 4 esquinas
    const CORNER_LABELS = { nw: 'NW', ne: 'NE', sw: 'SW', se: 'SE' }
    Object.entries(CORNER_LABELS).forEach(([key, label]) => {
      const [lat, lon] = workingCorners[key]
      const el = createHandleEl(label)
      const marker = new maplibregl.Marker({
        element: el,
        draggable: true,
      })
        .setLngLat([lon, lat])
        .addTo(map)

      marker.on('drag', () => {
        const ll = marker.getLngLat()
        setWorkingCorners(prev => ({
          ...(prev || workingCornersRef.current),
          [key]: [ll.lat, ll.lng],
        }))
      })
      marker.on('dragend', () => {
        const ll = marker.getLngLat()
        // Usamos workingCornersRef.current (siempre actualizado) en lugar
        // del closure stale de workingCorners. Sin este fix, soltar una
        // esquina llamaba al backend con valores viejos de las otras 3
        // esquinas — sólo se movía la actual, las demás "se atascaban".
        const updated = {
          ...workingCornersRef.current,
          [key]: [ll.lat, ll.lng],
        }
        setWorkingCorners(updated)
        if (onGeoreferenceChange) {
          onGeoreferenceChange(updated)
        }
      })

      handleMarkersRef.current[key] = marker
    })

    return () => {
      Object.values(handleMarkersRef.current).forEach(m => m.remove())
      handleMarkersRef.current = {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, mode, floorPlan?.id, !!workingCorners])
  // Incluimos !!workingCorners (no el objeto completo) para que el effect
  // dispare UNA vez cuando workingCorners pasa de null → algo. Una vez
  // creados los handles, el drag los actualiza directamente sin re-crear.

  // ── Camera markers (modos navigate y edit) ─────────────────────────────
  // En 'edit' las cámaras son draggables — el operador las posiciona sobre
  // el plano semitransparente y cada drop dispara onCameraMove con las
  // nuevas coords lat/lon (las x_pct/y_pct las recalcula el backend al
  // recibir la posición proyectada).
  useEffect(() => {
    if (!mapReady || !mapRef.current) return
    const map = mapRef.current

    // Remover markers anteriores
    Object.values(cameraMarkersRef.current).forEach(m => m.remove())
    cameraMarkersRef.current = {}

    if (mode !== 'navigate' && mode !== 'edit') return

    // Set para lookup rápido de incidentes
    const incidentSet = new Set(incidentCameraIds)

    // Helpers de proyección forward (x_pct/y_pct → lat/lon) usando 4 esquinas.
    // Bilineal: top = lerp(NW, NE, x_pct), bot = lerp(SW, SE, x_pct),
    //          point = lerp(top, bot, y_pct).
    const pctToLatLon = (xPct, yPct) => {
      if (!workingCornersRef.current) return null
      const c = workingCornersRef.current
      const lerp = (a, b, t) => a + (b - a) * t
      const topLat = lerp(c.nw[0], c.ne[0], xPct)
      const topLon = lerp(c.nw[1], c.ne[1], xPct)
      const botLat = lerp(c.sw[0], c.se[0], xPct)
      const botLon = lerp(c.sw[1], c.se[1], xPct)
      return [lerp(topLat, botLat, yPct), lerp(topLon, botLon, yPct)]
    }
    // Inversa BILINEAL exacta (Newton). Antes era affine — causaba drift
    // entre el click del operador y donde renderiza la cámara después.
    const latLonToPct = (lat, lon) => {
      const c = workingCornersRef.current
      if (!c) return null
      const pct = _invertBilinearLatLon(c, lat, lon)
      if (!pct) return null
      return [
        Math.max(0, Math.min(1, pct.xPct)),
        Math.max(0, Math.min(1, pct.yPct)),
      ]
    }

    cameras.forEach(cam => {
      // Solo renderizar cámaras posicionadas — las que tienen lat/lon
      // calculadas por el backend. Las cámaras sin posicionar quedan
      // en el sidebar (CamerasStrip) y se arrastran desde ahí al plano.
      // Decisión Paulo 2026-06-12: flujo limpio — el operador decide cuándo
      // poner cada cámara en el plano, no auto-aparición.
      let latLon = null
      if (cam.lat != null && cam.lon != null) {
        latLon = [cam.lat, cam.lon]
      }
      if (!latLon) return

      // CRÍTICO: el wrap debe ser un PUNTO (width:0 height:0). Antes era
      // 'position: relative' sin tamaño y el browser le daba width:100% del
      // map container (1617px) — los hijos con translate(-50%,-50%) se
      // centraban en el TOP-LEFT del wrap (sería 0,0 del wrap), no en el
      // punto real del marker. Resultado: el cono y el icono "3" se
      // renderizaban a la izquierda de la pantalla aunque MapLibre tenía
      // el marker correctamente en (lat, lon). Causa del "no engancha".
      const wrap = document.createElement('div')
      wrap.style.cssText = 'position: relative; width: 0; height: 0;'

      // Cono de visión escalado a metros reales del plano.
      // Convertimos effective_range_m a píxeles del MAPA usando metersPerPixel
      // del MapLibre al zoom actual (project() de 2 puntos a 1m de distancia
      // y medimos el delta píxel). Eso hace que el cono cambie con el zoom
      // como cualquier overlay geo-escalado, y respete fov/range/rotación
      // que el operador ajusta en el drawer.
      if (cam.effective_range_m && cam.fov_horizontal_deg != null && cam.lat != null) {
        // 1m en píxeles al zoom actual = distancia píxel entre cam y cam+1m_north
        const p1 = map.project([cam.lon, cam.lat])
        const oneMeterLat = 1 / 111320  // grados por metro aprox (lat-independent enough)
        const p2 = map.project([cam.lon, cam.lat + oneMeterLat])
        const pxPerMeter = Math.abs(p2.y - p1.y) || 1
        const rangePx = Math.max(20, cam.effective_range_m * pxPerMeter)
        const cone = createConeEl({
          // El backend serializa `floor_plan: { rotation_deg }` nested.
          // En live-preview el padre puede mergear partial con la misma
          // shape (draft.floor_plan?.rotation_deg). Aceptamos ambos por
          // resiliencia.
          rotation_deg: (cam.floor_plan?.rotation_deg ?? cam.floor_plan_rotation_deg) || 0,
          fov_deg     : cam.fov_horizontal_deg || 70,
          range_px    : rangePx,
        })
        wrap.appendChild(cone)
      }

      // Ícono de la cámara
      const iconEl = createCameraEl({
        channel    : cam.channel,
        isSelected : cam.id === selectedCameraId,
        hasIncident: incidentSet.has(cam.id),
      })
      iconEl.style.cursor = mode === 'edit' ? 'move' : 'pointer'
      iconEl.addEventListener('click', (e) => {
        e.stopPropagation()
        if (onCameraSelect) onCameraSelect(cam)
      })
      wrap.appendChild(iconEl)

      const marker = new maplibregl.Marker({
        element: wrap,
        anchor: 'center',
        draggable: mode === 'edit',
      })
        .setLngLat([latLon[1], latLon[0]])
        .addTo(map)

      if (mode === 'edit') {
        marker.on('dragend', () => {
          const ll = marker.getLngLat()
          const pct = latLonToPct(ll.lat, ll.lng)
          if (pct && onCameraMove) {
            onCameraMove(cam.id, pct[0], pct[1])
          }
        })
      }

      cameraMarkersRef.current[cam.id] = marker
    })

    return () => {
      Object.values(cameraMarkersRef.current).forEach(m => m.remove())
      cameraMarkersRef.current = {}
    }
  }, [
    mapReady, mode, cameras, selectedCameraId,
    incidentCameraIds, onCameraSelect,
  ])

  // ── Drop handler (HTML5 drag-and-drop desde sidebar) ───────────────────
  // Cuando el operador suelta una cámara arrastrada desde el sidebar de
  // CamerasStrip sobre el mapa, convertimos coords píxel del cursor a
  // lat/lon vía map.unproject(), luego a x_pct/y_pct con la proyección
  // inversa de las esquinas del plano.
  const handleDrop = useCallback((e) => {
    e.preventDefault()
    if (mode !== 'edit' || !mapRef.current || !workingCornersRef.current) return
    const cameraId = parseInt(e.dataTransfer.getData('text/plain'), 10)
    if (!cameraId) return
    const rect = containerRef.current.getBoundingClientRect()
    const px = e.clientX - rect.left
    const py = e.clientY - rect.top
    const ll = mapRef.current.unproject([px, py])
    // Inversa BILINEAL exacta (misma fórmula que el backend).
    const c = workingCornersRef.current
    const pct = _invertBilinearLatLon(c, ll.lat, ll.lng)
    if (!pct) return
    const { xPct, yPct } = pct
    if (onCameraDrop) {
      onCameraDrop(cameraId,
        Math.max(0, Math.min(1, xPct)),
        Math.max(0, Math.min(1, yPct)),
      )
    }
  }, [mode, onCameraDrop])

  const handleDragOver = useCallback((e) => {
    if (mode === 'edit') {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }, [mode])

  // ── Render container ───────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      style={{
        width: '100%',
        height,
        position: 'relative',
        background: '#1f2937',
      }}
    >
      {/* Keyframes para animación pulse de incidentes */}
      <style>{`
        @keyframes pulse-ring {
          0%   { box-shadow: 0 0 0 0 rgba(217, 48, 37, 0.6); }
          70%  { box-shadow: 0 0 0 14px rgba(217, 48, 37, 0); }
          100% { box-shadow: 0 0 0 0 rgba(217, 48, 37, 0); }
        }
      `}</style>
    </div>
  )
}
